import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, ButtonBase, Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import type { FC, ReactNode } from "react";

const COLUMNS = 4;
const TILE_HEIGHT = 52;
const ICON_SIZE = 20;
const PILL_SIZE = 22;

export type FormDrawerTile<TKey extends string> = {
  readonly key: TKey;
  readonly icon: ReactNode;
  readonly label: string;
  /** Every item of the tile, saved or not. */
  readonly count?: number;
  readonly hasError?: boolean;
};

type Props<TKey extends string> = {
  readonly tiles: FormDrawerTile<TKey>[];
  readonly activeKey: TKey;
  readonly onSelect: (key: TKey) => void;
};

const TilePill: FC<{ readonly count: number; readonly hasError: boolean; readonly isActive: boolean }> = ({
  count,
  hasError,
  isActive,
}) => {
  if (hasError) {
    return <ErrorOutlineIcon sx={{ flexShrink: 0, fontSize: ICON_SIZE, color: "error.main" }} />;
  }

  if (count === 0) {
    return null;
  }

  return (
    <Flex
      component="span"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      minWidth={PILL_SIZE}
      height={PILL_SIZE}
      px={Spacing.S}
      borderRadius={Radius.MAX}
      sx={(theme) => ({ bgcolor: isActive ? "primary.light" : theme.colors.textTertiary, color: theme.colors.darkGrey })}
    >
      <Typography component="span" fontSize={12} fontWeight={700} color="inherit" lineHeight={1}>
        {count}
      </Typography>
    </Flex>
  );
};

export const FormDrawerTiles = <TKey extends string>({ tiles, activeKey, onSelect }: Props<TKey>) => (
  <Box display="grid" gridTemplateColumns={`repeat(${COLUMNS}, minmax(0, 1fr))`} gap={Spacing.M}>
    {tiles.map(({ key, icon, label, count = 0, hasError = false }) => {
      const isActive = key === activeKey;

      return (
        <ButtonBase
          key={key}
          aria-pressed={isActive}
          onClick={() => onSelect(key)}
          sx={(theme) => ({
            justifyContent: "flex-start",
            gap: Spacing.M,
            height: TILE_HEIGHT,
            px: Spacing.L,
            border: 1,
            borderColor: isActive ? "primary.main" : "grey.400",
            borderRadius: Radius.L,
            bgcolor: isActive ? theme.colors.brandLighter : "common.white",
            transition: "border-color 150ms",
            "&:hover": { borderColor: "primary.main" },
            "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
          })}
        >
          <Flex
            flexShrink={0}
            color={isActive ? "primary.main" : "text.secondary"}
            sx={{ "& svg": { width: ICON_SIZE, height: ICON_SIZE } }}
          >
            {icon}
          </Flex>

          <Typography variant="body2" fontWeight={isActive ? 700 : 400} noWrap flex={1} minWidth={0} textAlign="left">
            {label}
          </Typography>

          <TilePill count={count} hasError={hasError} isActive={isActive} />
        </ButtonBase>
      );
    })}
  </Box>
);
